/**
 * Client-side AI service to replace Workers AI
 * Supports multiple AI providers with streaming responses
 */

import { localStorage } from '../browser-storage/localStorage';
import { GoogleGenAI } from '@google/genai';

export interface AIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AIStreamChunk {
  content: string;
  done: boolean;
}

export interface AIProvider {
  name: string;
  baseUrl: string;
  requiresApiKey: boolean;
  models: string[];
  supportsStreaming: boolean;
}

export interface AIRequest {
  messages: AIMessage[];
  model: string;
  provider: string;
  stream?: boolean;
  temperature?: number;
  maxTokens?: number;
}

export interface AIResponse {
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

// Supported AI providers
export const AI_PROVIDERS: Record<string, AIProvider> = {
  nanogpt: {
    name: 'NanoGPT',
    baseUrl: 'https://nano-gpt.com/api/v1',
    requiresApiKey: true,
    models: [
      'z-ai/glm-4.6',
      'zai-org/GLM-4.6-turbo',
      'zai-org/GLM-4.6-turbo:thinking',
      'z-ai/glm-4.6:thinking',
      'glm-4.1v-thinking-flashx',
      'openai/gpt-oss-120b',
      'deepseek-v3.2-exp-thinking-original',
      'Qwen/Qwen3-Coder-480B-A35B-Instruct-FP8'
    ],
    supportsStreaming: true
  },
  openai: {
    name: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    requiresApiKey: true,
    models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini'],
    supportsStreaming: true
  },
  openrouter: {
    name: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    requiresApiKey: true,
    models: ['openrouter/auto'],
    supportsStreaming: true
  },
  'google-ai-studio': {
    name: 'Google AI Studio',
    baseUrl: '',
    requiresApiKey: true,
    models: ['models/gemma-3-27b-it', 'gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.5-pro'],
    supportsStreaming: true
  }
};

export class ClientAIService {
  private apiKeys = new Map<string, string>();

  constructor() {
    this.loadApiKeys();
    // Attempt dev-time auto-load from Vite env if no keys are set
    // Only in development builds; avoids overwriting existing stored keys
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.autoLoadFromEnvIfDev();
  }

  private async autoLoadFromEnvIfDev(): Promise<void> {
    try {
      // Access Vite env safely in both dev and prod builds
      const env = (typeof import.meta !== 'undefined' ? (import.meta as any).env : {}) || {};
      const candidates: Array<{ provider: 'nanogpt'|'google-ai-studio'; key?: string }>= [
        { provider: 'nanogpt', key: env.NANOGPT_API_KEY || env.VITE_NANOGPT_API_KEY },
        { provider: 'google-ai-studio', key: env.GEMINI_API_KEY || env.VITE_GEMINI_API_KEY },
      ];

      let loadedCount = 0;
      for (const { provider, key } of candidates) {
        const hasKey = key && typeof key === 'string' && key.trim().length > 0;
        const alreadyStored = await this.hasApiKey(provider);

        if (!hasKey) continue;
        if (alreadyStored) continue;

        await this.setApiKey(provider, key!.trim());
        loadedCount++;
      }

      if (loadedCount > 0) {
        console.log(`[clientAI] Auto-loaded ${loadedCount} API key(s) from environment`);
      }
    } catch (err) {
      console.warn('[clientAI] autoLoadFromEnvIfDev failed:', err);
    }
  }

	  /**
	   * Get API key from Vite environment variables (works in DEV and PROD builds)
	   * Does not persist to storage; only caches in-memory for this session
	   */
	  private getEnvApiKey(provider: string): string | null {
	    try {
	      const env = (typeof import.meta !== 'undefined' ? (import.meta as any).env : {}) || {};
	      switch (provider) {
	        case 'openai':
	          return (env.VITE_OPENAI_API_KEY as string | undefined) || null;
	        case 'anthropic':
	          return (env.VITE_ANTHROPIC_API_KEY as string | undefined) || null;
	        case 'google':
	          return (env.VITE_GOOGLE_API_KEY as string | undefined) || null;
        case 'nanogpt':
          return (env.NANOGPT_API_KEY as string | undefined) || (env.VITE_NANOGPT_API_KEY as string | undefined) || null;
        case 'google-ai-studio':
          return (env.GEMINI_API_KEY as string | undefined) || (env.VITE_GEMINI_API_KEY as string | undefined) || null;
	        default:
	          return null;
	      }
	    } catch {
	      return null;
	    }
	  }


  /**
   * Set API key for a provider
   */
  async setApiKey(provider: string, apiKey: string): Promise<void> {
    if (!AI_PROVIDERS[provider]) {
      throw new Error(`Unknown provider: ${provider}`);
    }

    // Encrypt and store API key
    const encryptedKey = await this.encryptApiKey(apiKey);
    await localStorage.put(`api_key_${provider}`, encryptedKey);
    this.apiKeys.set(provider, apiKey);
  }

  /**
   * Get API key for a provider
   */
  async getApiKey(provider: string): Promise<string | null> {
    if (this.apiKeys.has(provider)) {
      return this.apiKeys.get(provider)!;
    }

    const encryptedKey = await localStorage.get<string>(`api_key_${provider}`);
    if (encryptedKey) {
      try {
        const decryptedKey = await this.decryptApiKey(encryptedKey);
        this.apiKeys.set(provider, decryptedKey);
        return decryptedKey;
      } catch (error) {
        console.warn(`Failed to decrypt API key for ${provider}:`, error);
        await localStorage.delete(`api_key_${provider}`);
      }
    }

    // Fallback: Read from environment (Vite) without persisting
    const envKey = this.getEnvApiKey(provider);
    if (envKey && typeof envKey === 'string' && envKey.trim().length > 0) {
      this.apiKeys.set(provider, envKey.trim());
      return envKey.trim();
    }

    return null;
  }

  /**
   * Check if API key is set for a provider
   */
  async hasApiKey(provider: string): Promise<boolean> {
    const apiKey = await this.getApiKey(provider);
    return apiKey !== null && apiKey.length > 0;
  }

  /**
   * Remove API key for a provider
   */
  async removeApiKey(provider: string): Promise<void> {
    this.apiKeys.delete(provider);
    await localStorage.delete(`api_key_${provider}`);
  }

  /**
   * Generate AI response
   */
  async generateResponse(request: AIRequest): Promise<AIResponse> {
    const provider = AI_PROVIDERS[request.provider];
    if (!provider) {
      throw new Error(`Unknown provider: ${request.provider}`);
    }

    if (request.provider === 'google-ai-studio') {
      const apiKey = await this.getApiKey('google-ai-studio');
      if (!apiKey) throw new Error('API key not set for provider: google-ai-studio');
      const genAI = new GoogleGenAI({ apiKey });
      const contents = request.messages.map(m => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: [{ text: m.content }] }));
      const res: any = await genAI.models.generateContent({ model: request.model || 'models/gemma-3-27b-it', contents });
      const text: string = (res.output_text as string) || (typeof res.response?.text === 'function' ? res.response.text() : '') || '';
      return { content: text };
    }

    const apiKey = await this.getApiKey(request.provider);
    if (!apiKey) {
      throw new Error(`API key not set for provider: ${request.provider}`);
    }

    if (request.stream) {
      throw new Error('Use generateStreamingResponse for streaming requests');
    }

    const response = await this.makeRequest(request, apiKey, provider);
    return this.parseResponse(response, request.provider);
  }

  /**
   * Generate streaming AI response
   */
  async* generateStreamingResponse(request: AIRequest): AsyncGenerator<AIStreamChunk> {
    const provider = AI_PROVIDERS[request.provider];
    if (!provider) {
      throw new Error(`Unknown provider: ${request.provider}`);
    }

    if (!provider.supportsStreaming) {
      throw new Error(`Provider ${request.provider} does not support streaming`);
    }

    if (request.provider === 'google-ai-studio') {
      const apiKey = await this.getApiKey('google-ai-studio');
      if (!apiKey) throw new Error('API key not set for provider: google-ai-studio');
      const genAI = new GoogleGenAI({ apiKey });
      const contents = request.messages.map(m => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: [{ text: m.content }] }));
      const streamRes: any = await genAI.models.generateContentStream({ model: request.model || 'models/gemma-3-27b-it', contents });
      for await (const event of streamRes.stream) {
        const partText = typeof (event as any).text === 'function' ? (event as any).text() : '';
        if (partText) {
          yield { content: partText, done: false };
        }
      }
      yield { content: '', done: true };
      return;
    }

    const apiKey = await this.getApiKey(request.provider);
    if (!apiKey) {
      throw new Error(`API key not set for provider: ${request.provider}`);
    }

    const response = await this.makeStreamingRequest(request, apiKey, provider);
    yield* this.parseStreamingResponse(response, request.provider);
  }

  /**
   * Get available models for a provider
   */
  getModels(provider: string): string[] {
    const providerConfig = AI_PROVIDERS[provider];
    return providerConfig ? providerConfig.models : [];
  }

  /**
   * Get all available providers
   */
  getProviders(): AIProvider[] {
    return Object.values(AI_PROVIDERS);
  }

  /**
   * Test API key validity
   */
  async testApiKey(provider: string, apiKey?: string): Promise<boolean> {
    try {
      const keyToTest = apiKey || await this.getApiKey(provider);
      if (!keyToTest) return false;

      const testRequest: AIRequest = {
        messages: [{ role: 'user', content: 'Hello' }],
        model: this.getModels(provider)[0],
        provider,
        maxTokens: 10
      };

      await this.generateResponse(testRequest);
      return true;
    } catch (error) {
      console.warn(`API key test failed for ${provider}:`, error);
      return false;
    }
  }

  // Private methods

  private async loadApiKeys(): Promise<void> {
    for (const provider of Object.keys(AI_PROVIDERS)) {
      await this.getApiKey(provider); // This will load and cache the key
    }
  }

  private async makeRequest(request: AIRequest, apiKey: string, provider: AIProvider): Promise<Response> {
    const headers = this.getHeaders(request.provider, apiKey);
    const body = this.formatRequestBody(request, provider);

    const endpoint = this.getEndpoint(provider, request.provider);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`AI API request failed: ${response.status} ${errorText}`);
    }

    return response;
  }

  private async makeStreamingRequest(request: AIRequest, apiKey: string, provider: AIProvider): Promise<Response> {
    const headers = this.getHeaders(request.provider, apiKey);
    const body = this.formatRequestBody({ ...request, stream: true }, provider);

    const endpoint = this.getEndpoint(provider, request.provider);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`AI API streaming request failed: ${response.status} ${errorText}`);
    }

    return response;
  }

  private getHeaders(provider: string, apiKey: string): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (provider === 'nanogpt' || provider === 'openai' || provider === 'openrouter') {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }
    return headers;
  }

  private getEndpoint(provider: AIProvider, providerName: string): string {
    switch (providerName) {
      case 'nanogpt':
      case 'openai':
      case 'openrouter':
        return `${provider.baseUrl}/chat/completions`;
      default:
        throw new Error(`Unknown provider: ${providerName}`);
    }
  }

  private formatRequestBody(request: AIRequest, _provider: AIProvider): unknown {
    switch (request.provider) {
      case 'nanogpt':
      case 'openai':
      case 'openrouter':
        return {
          model: request.model,
          max_tokens: request.maxTokens || 4096,
          temperature: request.temperature || 0.7,
          messages: request.messages,
          stream: request.stream || false
        };
      default:
        throw new Error(`Unknown provider: ${request.provider}`);
    }
  }

  private async parseResponse(response: Response, provider: string): Promise<AIResponse> {
    const data = await response.json();

    switch (provider) {
      case 'nanogpt':
      case 'openai':
      case 'openrouter':
        return {
          content: data.choices?.[0]?.message?.content || '',
          usage: data.usage ? {
            promptTokens: data.usage.prompt_tokens,
            completionTokens: data.usage.completion_tokens,
            totalTokens: data.usage.total_tokens
          } : undefined
        };
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }
  }

  private async* parseStreamingResponse(response: Response, provider: string): AsyncGenerator<AIStreamChunk> {
    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              yield { content: '', done: true };
              return;
            }

            try {
              const parsed = JSON.parse(data);
              const content = this.extractStreamContent(parsed, provider);
              if (content) {
                yield { content, done: false };
              }
            } catch (error) {
              console.warn('Failed to parse streaming chunk:', error);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private extractStreamContent(data: unknown, provider: string): string {
    switch (provider) {
      case 'nanogpt':
      case 'openai':
      case 'openrouter':
        return (data as any)?.choices?.[0]?.delta?.content || '';
      default:
        return '';
    }
  }

  private async encryptApiKey(apiKey: string): Promise<string> {
    // Simple encryption using Web Crypto API
    // In production, you'd want more robust encryption
    const encoder = new TextEncoder();
    const data = encoder.encode(apiKey);
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode('app-builder-encryption-key-32b'),
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  private async decryptApiKey(encryptedKey: string): Promise<string> {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();

    const combined = new Uint8Array(
      atob(encryptedKey).split('').map(char => char.charCodeAt(0))
    );

    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode('app-builder-encryption-key-32b'),
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );

    return decoder.decode(decrypted);
  }

  // NanoGPT image generation
  async generateImageNanoGPT(params: { prompt: string; size?: '256x256'|'512x512'|'1024x1024'|string; n?: number; user?: string; response_format?: 'url'|'b64_json' }): Promise<any> {
    const apiKey = await this.getApiKey('nanogpt');
    if (!apiKey) throw new Error('API key not set for provider: nanogpt');

    const body = {
      model: 'qwen-image',
      prompt: params.prompt,
      n: params.n ?? 1,
      size: params.size ?? '512x512',
      response_format: params.response_format ?? 'url',
      user: params.user
    };

    const resp = await fetch('https://nano-gpt.com/api/v1/images/generations', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`NanoGPT image generation failed: ${resp.status} ${text}`);
    }

    return resp.json();
  }

}

// Export singleton instance
export const clientAI = new ClientAIService();

// Dev-only helper to set keys from browser console without exposing UI
try {
  const isDev = typeof import.meta !== 'undefined' && !!(import.meta as any).env?.DEV;
  if (typeof window !== 'undefined' && isDev) {
    (window as any).__APP_BUILDER_SET_API_KEY__ = async (provider: 'nanogpt'|'google-ai-studio'|'openai'|'anthropic'|'google', key: string) => {
      await clientAI.setApiKey(provider, key);
      return clientAI.testApiKey(provider);
    };
    // eslint-disable-next-line no-console
    console.info('[clientAI] Dev helper available: __APP_BUILDER_SET_API_KEY__(provider, key)');
  }
} catch { /* noop */ }

